import React, { useEffect, useMemo, useState } from "react";
import {
  createRemision,
  finalizeRemision,
  getRemisionPdf,
  listRemisiones,
  markRemisionDelivered,
  updateRemision,
  updateRemisionGuide,
} from "../../api/remisionesApi";
import "./RemisionGuideModal.css";

const PROJECTS = [
  "Licencias de Conducir Quintana Roo",
  "Tarjetas de Circulación",
];

const CUSTOM_SPARE_PART = {
  id: "REFACCION_CUSTOM",
  proyecto: "TODOS",
  nombre: "Refacción",
  numeroParte: "",
  marca: "",
  unidad: "PZA",
  requiereFolios: false,
  capturaLibre: true,
};

function appendCustomSparePart(catalog = []) {
  const items = Array.isArray(catalog)
    ? catalog
    : [];

  if (
    items.some(
      (item) =>
        item.id === CUSTOM_SPARE_PART.id
    )
  ) {
    return items;
  }

  return [
    ...items,
    CUSTOM_SPARE_PART,
  ];
}

const CATALOG_API_BASE =
  "https://0n4eu7r5g0.execute-api.us-east-1.amazonaws.com/dev";

async function fetchCatalog(path, attempts = 3) {
  let lastError;

  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      const url = `${CATALOG_API_BASE}${path}`;

      const response = await fetch(url, {
        method: "GET",
        cache: "no-store",
        headers: {
          Accept: "application/json",
        },
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          payload?.error ||
            payload?.message ||
            `HTTP ${response.status} al cargar ${path}`
        );
      }

      return Array.isArray(payload?.items) ? payload.items : [];
    } catch (error) {
      lastError = error;

      if (attempt < attempts) {
        await new Promise((resolve) =>
          setTimeout(resolve, attempt * 500)
        );
      }
    }
  }

  throw lastError;
}

function normalizeInsumo(item) {
  const id = item.id || item.insumoId || item.sk || "";

  return {
    ...item,
    id,
    proyecto:
      item.proyecto || "Licencias de Conducir Quintana Roo",
    nombre:
      item.nombreOficial ||
      item.nombre ||
      item.descripcionComercial ||
      id,
    numeroParte:
      item.numeroParteProveedor ||
      item.numeroParte ||
      item.partNumber ||
      "",
    marca: item.marca || "",
    unidad: item.unidad || "PZA",
    requiereFolios:
      typeof item.requiereFolios === "boolean"
        ? item.requiereFolios
        : id === "TARJETAS_PREIMPRESAS",
  };
}

function catalogDisplayName(item) {
  return String(
    item?.nombreCompleto ||
    item?.nombreOficial ||
    item?.descripcion ||
    item?.nombre ||
    ""
  ).trim();
}

function supervisorDisplayName(item) {
  return String(
    item?.nombreCompleto ||
    item?.nombreOficial ||
    item?.nombre ||
    ""
  ).trim();
}

function createId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createInitialHistoryFilters() {
  return {
    fechaDesde: "",
    fechaHasta: "",
    proyecto: "",
    municipio: "",
    oficina: "",
    status: "",
    supervisor: "",
    paqueteria: "",
    busqueda: "",
  };
}

function createInitialForm() {
  return {
    proyecto: PROJECTS[0],
    fechaEnvio: new Date().toISOString().slice(0, 10),
    municipio: "",
    oficina: "",
    supervisor: "",
    medioTransporte: "Paquetería",
    paqueteria: "",
    numeroGuia: "",
    observaciones: "",
  };
}

function createEmptyItem(catalog = []) {
  const first = catalog[0];

  if (!first) {
    return {
      rowId: createId(),
      catalogId: "",
      descripcion: "",
      numeroParte: "",
      marca: "",
      unidad: "PZA",
      cantidad: 1,
      requiereFolios: false,
      folioDesde: "",
      folioHasta: "",
      observaciones: "",
      capturaLibre: false,
    };
  }

  return {
    rowId: createId(),
    catalogId: first.id,
    descripcion: first.nombre,
    numeroParte: first.numeroParte,
    marca: first.marca,
    unidad: first.unidad,
    cantidad: first.requiereFolios ? 0 : 1,
    requiereFolios: first.requiereFolios,
    folioDesde: "",
    folioHasta: "",
    observaciones: "",
    capturaLibre: Boolean(first.capturaLibre),
  };
}

function calculateInclusiveQuantity(fromValue, toValue) {
  const from = Number(fromValue);
  const to = Number(toValue);

  if (!Number.isInteger(from) || !Number.isInteger(to)) return 0;
  if (from <= 0 || to <= 0 || to < from) return 0;

  return to - from + 1;
}

// REMISSION_DISTRIBUTION_PASO_2

function normalizeRemissionMaterialText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function getRemissionMaterialKey(item) {
  const text = normalizeRemissionMaterialText(
    [
      item?.catalogId,
      item?.descripcion,
      item?.numeroParte,
      item?.unidad,
    ].join(" ")
  );

  if (
    text.includes("TARJETA") ||
    text.includes("PLASTICO") ||
    text.includes("PLASTICOS") ||
    text.includes("PVC")
  ) {
    return "plasticos";
  }

  if (text.includes("RETRANSFER")) {
    return "retransfer";
  }

  if (
    text.includes("LAMINADOR") ||
    text.includes("LAMINADO")
  ) {
    return "laminador";
  }

  if (text.includes("RIBBON")) {
    return "ribbon";
  }

  if (
    text.includes("ROLLO") &&
    (
      text.includes("TERMICO") ||
      text.includes("TERMINAL")
    )
  ) {
    return "rollos";
  }

  return null;
}

// REMISSION_PRINTER_MODEL_PASO_21

function normalizeRemissionPrinterLabel(value) {
  const text = String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();

  if (text.includes("K30")) {
    return "K30 SWITF PRO";
  }

  if (
    text.includes("SMART") ||
    text.includes("51L")
  ) {
    return "SMART 51L";
  }

  return "";
}
function createEmptyMaterialTotals() {
  return {
    plasticos: 0,
    ribbon: 0,
    laminador: 0,
    retransfer: 0,
    rollos: 0,
  };
}
function statusLabel(status) {
  const labels = {
    BORRADOR: "Borrador",
    PENDIENTE_GUIA: "Pendiente de guía",
    EN_TRANSITO: "En tránsito",
    ENTREGADA: "Entregada",
    CANCELADA: "Cancelada",
  };

  return labels[status] || status || "Sin estatus";
}


async function printPdfAndClose(pdfWindow, pdfUrl) {
  if (!pdfWindow) {
    throw new Error(
      "El navegador bloqueó la pestaña del PDF. Habilita las ventanas emergentes."
    );
  }

  /*
   * Abrir directamente el PDF en el visor nativo del navegador.
   * No se usa fetch, iframe, botón personalizado ni impresión automática.
   */
  pdfWindow.location.replace(pdfUrl);

  return true;
}

export default function RemisionForm() {
  const [form, setForm] = useState(createInitialForm);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [historyFilters, setHistoryFilters] = useState(
    createInitialHistoryFilters
  );
  const [currentId, setCurrentId] = useState("");
  const [folio, setFolio] = useState("AUTO-FOLIO");
  const [message, setMessage] = useState("");
  const [messageTone, setMessageTone] = useState("info");
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(true);

  // REMISSION_TABS_PASO_1
  const [historyTab, setHistoryTab] = useState("resumen");
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState({
    open: false,
    url: "",
    folio: "",
    printing: false,
  });
  const [municipiosCatalog, setMunicipiosCatalog] = useState([]);
  const [oficinasCatalog, setOficinasCatalog] = useState([]);
  const [supervisoresCatalog, setSupervisoresCatalog] = useState([]);
  const [insumosCatalog, setInsumosCatalog] = useState([]);
  const [modelosCatalog, setModelosCatalog] = useState([]);
  const [rendimientosCatalog, setRendimientosCatalog] = useState([]);

  const selectedMunicipio = useMemo(
    () =>
      municipiosCatalog.find(
        (item) =>
          catalogDisplayName(item) === form.municipio
      ) || null,
    [municipiosCatalog, form.municipio]
  );

  const offices = useMemo(
    () =>
      oficinasCatalog.filter(
        (item) =>
          item.municipioId === selectedMunicipio?.id
      ),
    [oficinasCatalog, selectedMunicipio]
  );

  const selectedOffice = useMemo(
    () =>
      oficinasCatalog.find(
        (item) =>
          catalogDisplayName(item) === form.oficina
      ) || null,
    [oficinasCatalog, form.oficina]
  );

  const selectedModel = useMemo(
    () =>
      modelosCatalog.find(
        (item) => item.id === selectedOffice?.modeloImpresoraId
      ) || null,
    [modelosCatalog, selectedOffice]
  );

  const catalogForProject = useMemo(() => {
    const modelId = selectedOffice?.modeloImpresoraId;

    if (!modelId) {
      return appendCustomSparePart(
        insumosCatalog
      );
    }

    const applicableIds = new Set(
      rendimientosCatalog
        .filter(
          (item) =>
            item.modeloImpresoraId === modelId &&
            item.aplica !== false
        )
        .map((item) => item.insumoId)
    );

    if (!applicableIds.size) {
      return appendCustomSparePart(
        insumosCatalog
      );
    }

    return appendCustomSparePart(
      insumosCatalog.filter((item) =>
        applicableIds.has(item.id)
      )
    );
  }, [
    insumosCatalog,
    rendimientosCatalog,
    selectedOffice,
    form.proyecto,
  ]);

  const totalUnits = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total + Math.max(0, Number(item.cantidad) || 0),
        0
      ),
    [items]
  );

  const historyOptions = useMemo(() => {
    const unique = (values) =>
      [...new Set(
        values
          .map((value) => String(value || "").trim())
          .filter(Boolean)
      )].sort((a, b) =>
        a.localeCompare(b, "es", {
          sensitivity: "base",
        })
      );

    return {
      proyectos: unique(
        history.map((record) => record.proyecto)
      ),
      municipios: unique(
        history.map((record) => record.municipio)
      ),
      oficinas: unique(
        history
          .filter(
            (record) =>
              !historyFilters.municipio ||
              record.municipio ===
                historyFilters.municipio
          )
          .map((record) => record.oficina)
      ),
      supervisores: unique(
        history.map((record) => record.supervisor)
      ),
      paqueterias: unique(
        history.map((record) => record.paqueteria)
      ),
    };
  }, [history, historyFilters.municipio]);

  const filteredHistory = useMemo(() => {
    const normalizedSearch =
      historyFilters.busqueda
        .toLowerCase()
        .trim();

    return history.filter((record) => {
      const fecha =
        String(record.fechaEnvio || "").slice(0, 10);

      if (
        historyFilters.fechaDesde &&
        fecha < historyFilters.fechaDesde
      ) {
        return false;
      }

      if (
        historyFilters.fechaHasta &&
        fecha > historyFilters.fechaHasta
      ) {
        return false;
      }

      if (
        historyFilters.proyecto &&
        record.proyecto !== historyFilters.proyecto
      ) {
        return false;
      }

      if (
        historyFilters.municipio &&
        record.municipio !==
          historyFilters.municipio
      ) {
        return false;
      }

      if (
        historyFilters.oficina &&
        record.oficina !== historyFilters.oficina
      ) {
        return false;
      }

      if (
        historyFilters.status &&
        record.status !== historyFilters.status
      ) {
        return false;
      }

      if (
        historyFilters.supervisor &&
        record.supervisor !==
          historyFilters.supervisor
      ) {
        return false;
      }

      if (
        historyFilters.paqueteria &&
        record.paqueteria !==
          historyFilters.paqueteria
      ) {
        return false;
      }

      if (!normalizedSearch) return true;

      const searchableText = [
        record.folio,
        record.numeroGuia,
        record.proyecto,
        record.municipio,
        record.oficina,
        record.supervisor,
        record.paqueteria,
        record.status,
        statusLabel(record.status),
      ]
        .join(" ")
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [history, historyFilters]);

  const historyKpis = useMemo(() => {
    return filteredHistory.reduce(
      (accumulator, record) => {
        accumulator.total += 1;
        accumulator.unidades +=
          Number(record.totalUnidades || 0);

        if (record.status === "BORRADOR") {
          accumulator.borradores += 1;
        }

        if (record.status === "PENDIENTE_GUIA") {
          accumulator.pendientesGuia += 1;
        }

        if (record.status === "EN_TRANSITO") {
          accumulator.enTransito += 1;
        }

        if (record.status === "ENTREGADA") {
          accumulator.entregadas += 1;
        }

        return accumulator;
      },
      {
        total: 0,
        borradores: 0,
        pendientesGuia: 0,
        enTransito: 0,
        entregadas: 0,
        unidades: 0,
      }
    );
  }, [filteredHistory]);

  // REMISSION_DISTRIBUTION_PASO_2_DATA
  const distributionByMunicipality = useMemo(() => {
    const map = new Map();

    filteredHistory.forEach((record) => {
      /*
       * Solo movimientos que ya representan material fisico
       * preparado para salir, en transito o entregado.
       *
       * BORRADOR y CANCELADA no afectan distribucion.
       */
      if (
        record.status === "BORRADOR" ||
        record.status === "CANCELADA"
      ) {
        return;
      }

      // REMISSION_OFFICE_DISTRIBUTION_PASO_22
      const municipio =
        String(record.municipio || "Sin municipio").trim() ||
        "Sin municipio";

      const oficina =
        String(record.oficina || "Sin oficina").trim() ||
        "Sin oficina";

      /*
       * La unidad de agrupacion correcta es:
       * MUNICIPIO + OFICINA
       *
       * Esto evita mezclar, por ejemplo:
       * Benito Juarez Taxistas (SMART 51L)
       * con
       * Benito Juarez Transito (K30 SWITF PRO)
       */
      const distributionKey = `${municipio}__${oficina}`;

      if (!map.has(distributionKey)) {
        map.set(distributionKey, {
          municipio,
          oficina,
          remisiones: 0,
          modelos: new Set(),
          ...createEmptyMaterialTotals(),
        });
      }

      const target = map.get(distributionKey);

      target.remisiones += 1;


      /*
       * Modelo de impresora.
       * Primero intenta usar snapshot de la remision.
       * Si no existe, resuelve por catalogo de oficina.
       */
      const officeCatalogRecord =
        oficinasCatalog.find(
          (office) =>
            catalogDisplayName(office) === record.oficina
        ) || null;

      const printerCatalogRecord =
        modelosCatalog.find(
          (model) =>
            model.id === officeCatalogRecord?.modeloImpresoraId
        ) || null;

      const printerLabel =
        normalizeRemissionPrinterLabel(
          record.modeloImpresora ||
          record.modelo ||
          record.printerLabel ||
          printerCatalogRecord?.nombre ||
          printerCatalogRecord?.nombreOficial ||
          ""
        );

      if (printerLabel) {
        target.modelos.add(printerLabel);
      }

      (record.items || []).forEach((item) => {
        const key = getRemissionMaterialKey(item);

        if (!key) return;

        target[key] += Math.max(
          0,
          Number(item.cantidad) || 0
        );
      });
    });

    return [...map.values()]
      .map((row) => {
        const modelos = [...row.modelos];

        let modelo = "Sin asignar";

        if (modelos.length === 1) {
          modelo = modelos[0];
        }

        if (modelos.length > 1) {
          modelo = "SMART / K30";
        }

        return {
          ...row,
          modelo,
          soloSmart:
            modelos.length === 1 &&
            modelos[0] === "SMART 51L",
          soloK30:
            modelos.length === 1 &&
            modelos[0] === "K30 SWITF PRO",
        };
      })
      .sort((a, b) => {
        const municipioCompare =
          a.municipio.localeCompare(
            b.municipio,
            "es",
            { sensitivity: "base" }
          );

        if (municipioCompare !== 0) {
          return municipioCompare;
        }

        return a.oficina.localeCompare(
          b.oficina,
          "es",
          { sensitivity: "base" }
        );
      });
  }, [
    filteredHistory,
    oficinasCatalog,
    modelosCatalog,
  ]);

  const distributionTotals = useMemo(() => {
    return distributionByMunicipality.reduce(
      (accumulator, row) => {
        accumulator.remisiones += row.remisiones;
        accumulator.plasticos += row.plasticos;
        accumulator.ribbon += row.ribbon;
        accumulator.laminador += row.laminador;
        accumulator.retransfer += row.retransfer;
        accumulator.rollos += row.rollos;

        return accumulator;
      },
      {
        remisiones: 0,
        plasticos: 0,
        ribbon: 0,
        laminador: 0,
        retransfer: 0,
        rollos: 0,
      }
    );
  }, [distributionByMunicipality]);
  const hasInvalidFolioRange = items.some(
    (item) =>
      item.requiereFolios &&
      calculateInclusiveQuantity(
        item.folioDesde,
        item.folioHasta
      ) <= 0
  );

  const showMessage = (text, tone = "info") => {
    setMessage(text);
    setMessageTone(tone);
  };

  const updateHistoryFilter = (field, value) => {
    setHistoryFilters((current) => ({
      ...current,
      [field]: value,
      ...(field === "municipio"
        ? { oficina: "" }
        : {}),
    }));
  };

  const clearHistoryFilters = () => {
    setHistoryFilters(createInitialHistoryFilters());
  };

  const escapeExcelValue = (value) => {
    const text = String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");

    return text;
  };

  const exportHistoryExcel = () => {
    if (!filteredHistory.length) {
      showMessage(
        "No hay resultados para exportar.",
        "error"
      );
      return;
    }

    const generatedAt =
      new Date().toLocaleString("es-MX");

    const rows = filteredHistory
      .map(
        (record) => `
          <tr>
            <td>${escapeExcelValue(record.folio)}</td>
            <td>${escapeExcelValue(record.fechaEnvio)}</td>
            <td>${escapeExcelValue(record.proyecto)}</td>
            <td>${escapeExcelValue(record.municipio)}</td>
            <td>${escapeExcelValue(record.oficina)}</td>
            <td>${escapeExcelValue(record.supervisor)}</td>
            <td>${escapeExcelValue(record.paqueteria)}</td>
            <td>${escapeExcelValue(record.numeroGuia)}</td>
            <td>${escapeExcelValue(record.totalPartidas)}</td>
            <td>${escapeExcelValue(record.totalUnidades)}</td>
            <td>${escapeExcelValue(statusLabel(record.status))}</td>
          </tr>
        `
      )
      .join("");

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office"
            xmlns:x="urn:schemas-microsoft-com:office:excel"
            xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8" />
          <style>
            table {
              border-collapse: collapse;
              font-family: Arial, sans-serif;
              font-size: 11px;
            }
            th {
              background: #17365d;
              color: #ffffff;
              font-weight: bold;
            }
            th, td {
              border: 1px solid #b8c2ce;
              padding: 6px 8px;
              vertical-align: top;
            }
            .title {
              font-size: 16px;
              font-weight: bold;
              color: #17365d;
            }
            .meta {
              color: #475569;
            }
          </style>
        </head>
        <body>
          <table>
            <tr>
              <td colspan="11" class="title">
                Monitoreo de remisiones de envío
              </td>
            </tr>
            <tr>
              <td colspan="11" class="meta">
                Generado: ${escapeExcelValue(generatedAt)}
              </td>
            </tr>
            <tr>
              <td colspan="11" class="meta">
                Resultados: ${filteredHistory.length}
                | Unidades: ${historyKpis.unidades}
              </td>
            </tr>
            <tr>
              <th>Folio</th>
              <th>Fecha</th>
              <th>Proyecto</th>
              <th>Municipio</th>
              <th>Oficina</th>
              <th>Supervisor</th>
              <th>Paquetería</th>
              <th>Número de guía</th>
              <th>Partidas</th>
              <th>Unidades</th>
              <th>Estatus</th>
            </tr>
            ${rows}
          </table>
        </body>
      </html>
    `;

    const blob = new Blob(
      ["\ufeff", html],
      {
        type:
          "application/vnd.ms-excel;charset=utf-8;",
      }
    );

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download =
      `remisiones-${new Date()
        .toISOString()
        .slice(0, 10)}.xls`;

    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    URL.revokeObjectURL(url);

    showMessage(
      `Se exportaron ${filteredHistory.length} remisiones a Excel.`,
      "success"
    );
  };

  const exportHistoryPdf = () => {
    if (!filteredHistory.length) {
      showMessage(
        "No hay resultados para exportar.",
        "error"
      );
      return;
    }

    const frame = document.createElement("iframe");

    frame.style.position = "fixed";
    frame.style.right = "0";
    frame.style.bottom = "0";
    frame.style.width = "0";
    frame.style.height = "0";
    frame.style.border = "0";

    document.body.appendChild(frame);

    const rows = filteredHistory
      .map(
        (record) => `
          <tr>
            <td>${escapeExcelValue(record.folio)}</td>
            <td>${escapeExcelValue(record.fechaEnvio)}</td>
            <td>${escapeExcelValue(record.municipio)}</td>
            <td>${escapeExcelValue(record.oficina)}</td>
            <td>${escapeExcelValue(record.supervisor)}</td>
            <td>${escapeExcelValue(record.totalUnidades)}</td>
            <td>${escapeExcelValue(statusLabel(record.status))}</td>
          </tr>
        `
      )
      .join("");

    const printDocument = frame.contentWindow.document;

    printDocument.open();
    printDocument.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>Monitoreo de remisiones</title>
          <style>
            @page {
              size: landscape;
              margin: 12mm;
            }
            body {
              margin: 0;
              color: #172033;
              font-family: Arial, sans-serif;
              font-size: 10px;
            }
            h1 {
              margin: 0 0 4px;
              font-size: 19px;
            }
            .meta {
              margin-bottom: 14px;
              color: #64748b;
            }
            .kpis {
              display: grid;
              grid-template-columns: repeat(6, 1fr);
              gap: 8px;
              margin-bottom: 14px;
            }
            .kpi {
              border: 1px solid #cbd5e1;
              padding: 8px;
            }
            .kpi span {
              display: block;
              color: #64748b;
              font-size: 8px;
              text-transform: uppercase;
            }
            .kpi strong {
              display: block;
              margin-top: 4px;
              font-size: 15px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th {
              background: #17365d;
              color: #ffffff;
              text-align: left;
            }
            th, td {
              border: 1px solid #cbd5e1;
              padding: 5px;
              vertical-align: top;
            }
            tr {
              page-break-inside: avoid;
            }
          </style>
        </head>
        <body>
          <h1>Monitoreo de remisiones de envío</h1>
          <div class="meta">
            Generado el ${escapeExcelValue(
              new Date().toLocaleString("es-MX")
            )}
          </div>

          <div class="kpis">
            <div class="kpi">
              <span>Total</span>
              <strong>${historyKpis.total}</strong>
            </div>
            <div class="kpi">
              <span>Pendientes de guía</span>
              <strong>${historyKpis.pendientesGuia}</strong>
            </div>
            <div class="kpi">
              <span>En tránsito</span>
              <strong>${historyKpis.enTransito}</strong>
            </div>
            <div class="kpi">
              <span>Entregadas</span>
              <strong>${historyKpis.entregadas}</strong>
            </div>
            <div class="kpi">
              <span>Borradores</span>
              <strong>${historyKpis.borradores}</strong>
            </div>
            <div class="kpi">
              <span>Unidades</span>
              <strong>${historyKpis.unidades}</strong>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Municipio</th>
                <th>Oficina</th>
                <th>Supervisor</th>
                <th>Unidades</th>
                <th>Estatus</th>
              </tr>
            </thead>
            <tbody>
              ${rows}
            </tbody>
          </table>
        </body>
      </html>
    `);
    printDocument.close();

    frame.onload = () => {
      frame.contentWindow.focus();
      frame.contentWindow.print();

      window.setTimeout(() => {
        frame.remove();
      }, 1500);
    };
  };

  const loadHistory = async () => {
    setHistoryLoading(true);

    try {
      const result = await listRemisiones();
      setHistory(Array.isArray(result.items) ? result.items : []);
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadCatalogs = async () => {
    setCatalogLoading(true);

    try {
      const municipios = await fetchCatalog(
        "/catalogos/municipios?activo=true"
      );

      const oficinas = await fetchCatalog(
        "/catalogos/oficinas?activo=true"
      );

      const supervisores = await fetchCatalog(
        "/catalogos/supervisores?activo=true"
      );

      const insumos = await fetchCatalog(
        "/catalogos/insumos?activo=true"
      );

      const modelos = await fetchCatalog(
        "/catalogos/modelos-impresora?activo=true"
      );

      const rendimientos = await fetchCatalog(
        "/catalogos/rendimientos?activo=true"
      );

      const normalizedInsumos = insumos.map(normalizeInsumo);

      setMunicipiosCatalog(municipios);
      setOficinasCatalog(oficinas);
      setSupervisoresCatalog(supervisores);
      setInsumosCatalog(normalizedInsumos);
      setModelosCatalog(modelos);
      setRendimientosCatalog(rendimientos);

      const defaultMunicipio =
        municipios.find((item) => item.id === "OTHON_P_BLANCO") ||
        municipios[0] ||
        null;

      const defaultOffice =
        oficinas.find(
          (item) =>
            item.id === "OTHON_P_BLANCO" &&
            item.municipioId === defaultMunicipio?.id
        ) ||
        oficinas.find(
          (item) => item.municipioId === defaultMunicipio?.id
        ) ||
        null;

      const defaultSupervisor =
        supervisores.find(
          (item) =>
            item.oficinaId === defaultOffice?.id ||
            item.oficina === catalogDisplayName(defaultOffice)
        ) || null;

      setForm((current) => {
        if (currentId || current.municipio || current.oficina) {
          return current;
        }

        return {
          ...current,
          municipio: catalogDisplayName(defaultMunicipio),
          oficina: catalogDisplayName(defaultOffice),
          supervisor: supervisorDisplayName(defaultSupervisor),
        };
      });

      setItems((current) =>
        current.length
          ? current
          : [createEmptyItem(normalizedInsumos)]
      );
    } catch (error) {
      showMessage(
        `No se pudieron cargar los catálogos: ${error.message}`,
        "error"
      );
    } finally {
      setCatalogLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    loadCatalogs();
  }, []);

  const isTarjetasCirculacion =
    form.proyecto === "Tarjetas de Circulación";
  const updateForm = (field, value) => {
    setMessage("");

    if (field === "proyecto") {
      const isSatq =
        value === "Tarjetas de Circulación";

      setForm((current) => ({
        ...current,
        proyecto: value,
        municipio: isSatq
          ? "Gobierno del Estado de Quintana Roo"
          : current.municipio,
        oficina: isSatq
          ? "SATQ"
          : current.oficina,
        supervisor: isSatq
          ? "RECEPCIÓN SATQ"
          : current.supervisor,
      }));

      setItems([createEmptyItem(catalogForProject)]);
      return;
    }

    if (field === "municipio") {
      const municipio = municipiosCatalog.find(
        (item) => catalogDisplayName(item) === value
      );

      const nextOffices = oficinasCatalog.filter(
        (item) => item.municipioId === municipio?.id
      );

      const nextOffice = nextOffices[0] || null;

      const nextSupervisor =
        supervisoresCatalog.find(
          (item) =>
            item.oficinaId === nextOffice?.id ||
            item.oficina === catalogDisplayName(nextOffice)
        ) || null;

      setForm((current) => ({
        ...current,
        municipio: value,
        oficina: catalogDisplayName(nextOffice),
        supervisor: supervisorDisplayName(nextSupervisor),
      }));
      return;
    }

    if (field === "oficina") {
      const office = oficinasCatalog.find(
        (item) => catalogDisplayName(item) === value
      );

      const supervisor =
        supervisoresCatalog.find(
          (item) =>
            item.oficinaId === office?.id ||
            item.oficina === catalogDisplayName(office)
        ) || null;

      setForm((current) => ({
        ...current,
        oficina: value,
        supervisor: supervisorDisplayName(supervisor),
      }));
      return;
    }

    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const updateItem = (rowId, field, value) => {
    setMessage("");

    setItems((current) =>
      current.map((item) => {
        if (item.rowId !== rowId) return item;

        if (field === "catalogId") {
          const selected = catalogForProject.find(
            (catalogItem) => catalogItem.id === value
          );

          if (!selected) return item;

          return {
            ...item,
            catalogId: selected.id,
            descripcion: selected.capturaLibre
              ? ""
              : selected.nombre,
            numeroParte:
              selected.numeroParte || "",
            marca: selected.marca || "",
            unidad: selected.unidad || "PZA",
            requiereFolios:
              Boolean(selected.requiereFolios),
            capturaLibre:
              Boolean(selected.capturaLibre),
            cantidad: selected.requiereFolios
              ? 0
              : 1,
            folioDesde: "",
            folioHasta: "",
          };
        }

        const next = {
          ...item,
          [field]: value,
        };

        if (
          item.requiereFolios &&
          (field === "folioDesde" || field === "folioHasta")
        ) {
          next.cantidad = calculateInclusiveQuantity(
            field === "folioDesde" ? value : next.folioDesde,
            field === "folioHasta" ? value : next.folioHasta
          );
        }

        return next;
      })
    );
  };

  const addItem = () => {
    setItems((current) => [
      ...current,
      createEmptyItem(catalogForProject),
    ]);
  };

  const removeItem = (rowId) => {
    setItems((current) => {
      if (current.length === 1) return current;
      return current.filter((item) => item.rowId !== rowId);
    });
  };

  const validate = () => {
    if (
      !form.proyecto ||
      !form.fechaEnvio ||
      !form.municipio ||
      !form.oficina ||
      !form.supervisor
    ) {
      showMessage(
        "Completa los datos generales obligatorios.",
        "error"
      );
      return false;
    }

    if (!items.length) {
      showMessage("Agrega al menos un insumo.", "error");
      return false;
    }

    if (
      items.some(
        (item) =>
          !item.descripcion || Number(item.cantidad) <= 0
      )
    ) {
      showMessage(
        "Todos los insumos deben tener descripción y cantidad mayor a cero.",
        "error"
      );
      return false;
    }

    if (hasInvalidFolioRange) {
      showMessage(
        "Revisa los rangos de folios. El folio final debe ser mayor o igual al inicial.",
        "error"
      );
      return false;
    }

    return true;
  };

  const buildPayload = () => ({
    ...form,
    items: items.map((item) => ({
      catalogId: item.catalogId,
      descripcion: item.descripcion,
      numeroParte: item.numeroParte,
      marca: item.marca,
      unidad: item.unidad,
      cantidad: Number(item.cantidad) || 0,
      requiereFolios: Boolean(item.requiereFolios),
      capturaLibre: Boolean(item.capturaLibre),
      folioDesde: item.requiereFolios
        ? Number(item.folioDesde)
        : null,
      folioHasta: item.requiereFolios
        ? Number(item.folioHasta)
        : null,
      observaciones: item.observaciones || "",
    })),
  });

  const persistCurrent = async () => {
    const payload = buildPayload();

    const result = currentId
      ? await updateRemision(currentId, payload)
      : await createRemision(payload);

    const saved = result.item;

    setCurrentId(saved.id);
    setFolio(saved.folio);

    return saved;
  };

  const saveDraft = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const saved = await persistCurrent();
      showMessage(
        `Borrador ${saved.folio} guardado correctamente.`,
        "success"
      );
      await loadHistory();
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const openPdf = async (id) => {
    const pdfWindow = window.open("", "_blank");

    try {
      const result = await getRemisionPdf(id);

      if (pdfWindow) {
        pdfWindow.location.href = result.url;
      } else {
        window.location.href = result.url;
      }
    } catch (error) {
      if (pdfWindow) pdfWindow.close();
      showMessage(error.message, "error");
    }
  };

  const finalizeAndOpenPdf = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      const saved = await persistCurrent();
      const finalized = await finalizeRemision(saved.id);
      const pdf = await getRemisionPdf(saved.id);

      let previewUrl = pdf.url;

      /*
       * Convertimos el PDF a una URL local del navegador.
       * Esto permite imprimirlo desde el iframe sin navegar
       * fuera del módulo.
       */
      try {
        const pdfResponse = await fetch(pdf.url);

        if (!pdfResponse.ok) {
          throw new Error(
            `No se pudo cargar el PDF: ${pdfResponse.status}`
          );
        }

        const pdfBlob = await pdfResponse.blob();
        previewUrl = URL.createObjectURL(pdfBlob);
      } catch (previewError) {
        console.warn(
          "El PDF se mostrará desde su URL original:",
          previewError
        );
      }

      setCurrentId(finalized.item.id);
      setFolio(finalized.item.folio);

      setPdfPreview({
        open: true,
        url: previewUrl,
        folio: finalized.item.folio,
        printing: false,
      });

      await loadHistory();

      showMessage(
        `Remisión ${finalized.item.folio} finalizada correctamente.`,
        "success"
      );
    } catch (error) {
      showMessage(
        error.message ||
          "No se pudo finalizar la remisión.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview.printing) return;

    if (
      pdfPreview.url &&
      pdfPreview.url.startsWith("blob:")
    ) {
      URL.revokeObjectURL(pdfPreview.url);
    }

    setPdfPreview({
      open: false,
      url: "",
      folio: "",
      printing: false,
    });
  };

  const printPdfPreview = async () => {
    const frame = document.getElementById(
      "remission-pdf-preview-frame"
    );

    if (!frame) {
      showMessage(
        "No se encontró la vista previa del PDF.",
        "error"
      );
      return;
    }

    setPdfPreview((current) => ({
      ...current,
      printing: true,
    }));

    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();

      /*
       * print() regresa cuando el usuario imprime
       * o cierra el diálogo de impresión.
       */
      const currentUrl = pdfPreview.url;

      setPdfPreview({
        open: false,
        url: "",
        folio: "",
        printing: false,
      });

      newRemision();

      showMessage(
        "Remisión finalizada. El formulario está listo para una nueva captura.",
        "success"
      );

      if (
        currentUrl &&
        currentUrl.startsWith("blob:")
      ) {
        window.setTimeout(() => {
          URL.revokeObjectURL(currentUrl);
        }, 1500);
      }
    } catch (error) {
      setPdfPreview((current) => ({
        ...current,
        printing: false,
      }));

      showMessage(
        "El navegador no permitió imprimir desde la vista previa. Usa el botón de impresión del visor PDF.",
        "error"
      );
    }
  };

  const editDraft = (record) => {
    setCurrentId(record.id);
    setFolio(record.folio);

    setForm({
      proyecto: record.proyecto,
      fechaEnvio: record.fechaEnvio,
      municipio: record.municipio,
      oficina: record.oficina,
      supervisor: record.supervisor,
      medioTransporte: "Paquetería",
      paqueteria: record.paqueteria || "",
      numeroGuia: record.numeroGuia || "",
      observaciones: record.observaciones || "",
    });

    setItems(
      (record.items || []).map((item) => ({
        ...item,
        rowId: item.id || createId(),
        folioDesde: item.folioDesde ?? "",
        folioHasta: item.folioHasta ?? "",
        capturaLibre:
          item.catalogId === "REFACCION_CUSTOM" ||
          Boolean(item.capturaLibre),
      }))
    );

    showMessage(`Editando borrador ${record.folio}.`, "info");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const [guideModal, setGuideModal] = useState({
    open: false,
    record: null,
    paqueteria: "",
    numeroGuia: "",
    error: "",
  });

  const addGuide = (record) => {
    setGuideModal({
      open: true,
      record,
      paqueteria: record.paqueteria || "",
      numeroGuia: record.numeroGuia || "",
      error: "",
    });
  };

  const closeGuideModal = () => {
    if (loading) return;

    setGuideModal({
      open: false,
      record: null,
      paqueteria: "",
      numeroGuia: "",
      error: "",
    });
  };

  const updateGuideModal = (field, value) => {
    setGuideModal((current) => ({
      ...current,
      [field]: value,
      error: "",
    }));
  };

  const saveGuide = async () => {
    const record = guideModal.record;
    const paqueteria =
      String(guideModal.paqueteria || "").trim();

    const numeroGuia =
      String(guideModal.numeroGuia || "").trim();

    if (!record) {
      setGuideModal((current) => ({
        ...current,
        error: "No se encontró la remisión seleccionada.",
      }));
      return;
    }

    if (!paqueteria) {
      setGuideModal((current) => ({
        ...current,
        error: "Captura la empresa de paquetería.",
      }));
      return;
    }

    if (!numeroGuia) {
      setGuideModal((current) => ({
        ...current,
        error: "Captura el número de guía.",
      }));
      return;
    }

    setLoading(true);

    try {
      const result = await updateRemisionGuide(
        record.id,
        {
          paqueteria,
          numeroGuia,
        }
      );

      setGuideModal({
        open: false,
        record: null,
        paqueteria: "",
        numeroGuia: "",
        error: "",
      });

      showMessage(
        `Guía registrada y PDF ${result.item.folio} actualizado.`,
        "success"
      );

      await loadHistory();
    } catch (error) {
      setGuideModal((current) => ({
        ...current,
        error:
          error.message ||
          "No se pudo registrar el número de guía.",
      }));
    } finally {
      setLoading(false);
    }
  };

  const markDelivered = async (record) => {
    const receivedBy = window.prompt(
      "Nombre de quien recibió:",
      record.receivedBy || ""
    );

    if (receivedBy === null) return;

    const confirmed = window.confirm(
      `¿Confirmas que la remisión ${record.folio} fue entregada?`
    );

    if (!confirmed) return;

    setLoading(true);

    try {
      await markRemisionDelivered(record.id, {
        receivedBy: receivedBy.trim(),
        deliveredAt: new Date().toISOString(),
      });

      showMessage(
        `Remisión ${record.folio} marcada como entregada.`,
        "success"
      );
      await loadHistory();
    } catch (error) {
      showMessage(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const newRemision = () => {
    const initial = createInitialForm();

    const defaultMunicipio =
      municipiosCatalog.find(
        (item) => item.id === "OTHON_P_BLANCO"
      ) ||
      municipiosCatalog[0] ||
      null;

    const defaultOffice =
      oficinasCatalog.find(
        (item) =>
          item.id === "OTHON_P_BLANCO" &&
          item.municipioId === defaultMunicipio?.id
      ) ||
      oficinasCatalog.find(
        (item) => item.municipioId === defaultMunicipio?.id
      ) ||
      null;

    const defaultSupervisor =
      supervisoresCatalog.find(
        (item) =>
          item.oficinaId === defaultOffice?.id ||
          item.oficina === catalogDisplayName(defaultOffice)
      ) || null;

    setCurrentId("");
    setFolio("AUTO-FOLIO");
    setForm({
      ...initial,
      municipio: catalogDisplayName(defaultMunicipio),
      oficina: catalogDisplayName(defaultOffice),
      supervisor: supervisorDisplayName(defaultSupervisor),
    });
    setItems([createEmptyItem(insumosCatalog)]);
    setMessage("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="remission-module">
      <div className="remission-toolbar">
        <div>
          <div className="remission-kicker">
            Proveedores e insumos
          </div>
          <h1>Remisión de envío a oficina</h1>
         
        </div>

        
      </div>

      {message && (
        <div
          className={`remission-message remission-message-${messageTone}`}
        >
          {message}
        </div>
      )}

      <div className="remission-layout">
        <main className="remission-main">
          <section className="remission-card">
            <div className="remission-section-title">
              1. Datos generales de la remisión
            </div>

            <div className="remission-form-grid">
              <label>
                <span>Proyecto *</span>
                <select
                  value={form.proyecto}
                  onChange={(event) =>
                    updateForm("proyecto", event.target.value)
                  }
                  disabled={loading || catalogLoading}
                >
                  {PROJECTS.map((project) => (
                    <option key={project} value={project}>
                      {project}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                <span>Fecha de envío *</span>
                <input
                  type="date"
                  value={form.fechaEnvio}
                  onChange={(event) =>
                    updateForm("fechaEnvio", event.target.value)
                  }
                  disabled={loading || catalogLoading}
                />
              </label>

              <label>
                <span>Folio de remisión</span>
                <input value={folio} readOnly />
              </label>

              {!isTarjetasCirculacion ? (
                <>
                  <label>
                    <span>Municipio *</span>
                    <select
                      value={form.municipio}
                      onChange={(event) =>
                        updateForm("municipio", event.target.value)
                      }
                      disabled={loading || catalogLoading}
                    >
                      {municipiosCatalog.map((municipio) => (
                        <option
                          key={municipio.id}
                          value={catalogDisplayName(municipio)}
                        >
                          {catalogDisplayName(municipio)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Oficina destino *</span>
                    <select
                      value={form.oficina}
                      onChange={(event) =>
                        updateForm("oficina", event.target.value)
                      }
                      disabled={loading || catalogLoading}
                    >
                      {offices.map((office) => (
                        <option
                          key={office.id}
                          value={catalogDisplayName(office)}
                        >
                          {catalogDisplayName(office)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    <span>Supervisor de oficina *</span>
                    <input
                      value={form.supervisor}
                      onChange={(event) =>
                        updateForm("supervisor", event.target.value)
                      }
                      disabled={loading || catalogLoading}
                    />
                  </label>
                </>
              ) : (
                <div className="remission-satq-destination">
                  <div className="remission-satq-seal">
                    DESTINO INSTITUCIONAL
                  </div>

                  <div className="remission-satq-content">
                    <strong>
                      Gobierno del Estado de Quintana Roo
                    </strong>

                    <span>
                      Servicio de Administración Tributaria
                      del Estado de Quintana Roo
                    </span>

                    <b>SATQ</b>

                    <small>
                      El nombre y la firma de quien recibe se
                      asentarán directamente en la remisión impresa.
                    </small>
                  </div>
                </div>
              )}

              <label>
                <span>Medio de transporte</span>
                <input value="Paquetería" readOnly />
              </label>

              <label>
                <span>Empresa de paquetería</span>
                <input
                  placeholder="Se captura al realizar el envío"
                  value={form.paqueteria}
                  onChange={(event) =>
                    updateForm("paqueteria", event.target.value)
                  }
                  disabled={loading}
                />
                <small className="remission-field-help">
                  Opcional al generar la remisión.
                </small>
              </label>

              <label>
                <span>Número de guía</span>
                <input
                  placeholder="Se captura después de entregar el paquete"
                  value={form.numeroGuia}
                  onChange={(event) =>
                    updateForm("numeroGuia", event.target.value)
                  }
                  disabled={loading}
                />
                <small className="remission-field-help">
                  Opcional. Podrás agregarla cuando la paquetería
                  la asigne.
                </small>
              </label>

              <label className="remission-field-full">
                <span>Observaciones del envío</span>
                <textarea
                  rows="3"
                  maxLength="500"
                  placeholder="Información adicional, pendientes o condiciones de entrega"
                  value={form.observaciones}
                  onChange={(event) =>
                    updateForm(
                      "observaciones",
                      event.target.value
                    )
                  }
                  disabled={loading}
                />
              </label>
            </div>
          </section>

          <section className="remission-card">
            <div className="remission-section-heading">
              <div className="remission-section-title">
                2. Insumos a enviar
              </div>

              <button
                type="button"
                className="remission-button remission-button-add"
                onClick={addItem}
                disabled={loading}
              >
                + Agregar insumo
              </button>
            </div>

            <div className="remission-table-wrap">
              <table className="remission-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Insumo</th>
                    <th>Núm. de parte</th>
                    <th>Marca</th>
                    <th>Unidad</th>
                    <th>Cantidad</th>
                    <th>Folio desde</th>
                    <th>Folio hasta</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.rowId}>
                      <td>{index + 1}</td>

                      <td>
                        <div className="remission-insumo-selector">
                          <select
                            value={item.catalogId}
                            onChange={(event) =>
                              updateItem(
                                item.rowId,
                                "catalogId",
                                event.target.value
                              )
                            }
                            disabled={
                              loading ||
                              catalogLoading
                            }
                          >
                            {catalogForProject.map(
                              (catalogItem) => (
                                <option
                                  key={catalogItem.id}
                                  value={catalogItem.id}
                                >
                                  {catalogItem.capturaLibre
                                    ? "Refacción — captura libre"
                                    : catalogItem.nombre}
                                </option>
                              )
                            )}
                          </select>

                          {item.capturaLibre && (
                            <input
                              type="text"
                              className="remission-custom-spare-input"
                              value={item.descripcion}
                              onChange={(event) =>
                                updateItem(
                                  item.rowId,
                                  "descripcion",
                                  event.target.value
                                )
                              }
                              placeholder="Nombre de la refacción"
                              maxLength="180"
                              disabled={loading}
                              autoFocus
                            />
                          )}
                        </div>
                      </td>

                      <td>
                        <input
                          value={item.numeroParte}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "numeroParte",
                              event.target.value
                            )
                          }
                          placeholder={
                            item.capturaLibre
                              ? "Opcional"
                              : ""
                          }
                          maxLength="120"
                          disabled={loading}
                        />
                      </td>

                      <td>
                        <input
                          value={item.marca}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "marca",
                              event.target.value
                            )
                          }
                          disabled={loading}
                        />
                      </td>

                      <td>
                        <input
                          value={item.unidad}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "unidad",
                              event.target.value
                            )
                          }
                          disabled={loading}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          readOnly={item.requiereFolios}
                          value={item.cantidad}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "cantidad",
                              event.target.value
                            )
                          }
                          disabled={loading}
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="1"
                          disabled={
                            loading || !item.requiereFolios
                          }
                          value={item.folioDesde}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "folioDesde",
                              event.target.value
                            )
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="1"
                          disabled={
                            loading || !item.requiereFolios
                          }
                          value={item.folioHasta}
                          onChange={(event) =>
                            updateItem(
                              item.rowId,
                              "folioHasta",
                              event.target.value
                            )
                          }
                        />
                      </td>

                      <td>
                        <button
                          type="button"
                          className="remission-remove-button"
                          disabled={
                            loading || items.length === 1
                          }
                          onClick={() =>
                            removeItem(item.rowId)
                          }
                          title="Eliminar insumo"
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="remission-table-footer">
              

              <div className="remission-totals">
                <span>Partidas: {items.length}</span>
                <strong>
                  Total de unidades:{" "}
                  {totalUnits.toLocaleString("es-MX")}
                </strong>
              </div>
            </div>
          </section>

          <div className="remission-form-actions-wrap">
            <div className="remission-form-actions-info">
              <strong>Revisa la información antes de finalizar</strong>
              
            </div>

            <div className="remission-form-actions">
          <button
  type="button"
  className="remission-button remission-button-clear"
  onClick={() => {
    const hasCapturedData =
      Boolean(currentId) ||
      items.length > 1 ||
      items.some(
        (item) =>
          Number(item.cantidad) > 1 ||
          item.folioDesde ||
          item.folioHasta ||
          item.numeroParte ||
          item.marca
      ) ||
      Boolean(form.observaciones) ||
      Boolean(form.paqueteria) ||
      Boolean(form.numeroGuia);

    const confirmed =
      !hasCapturedData ||
      window.confirm(
        "¿Deseas limpiar todos los campos de esta remisión?"
      );

    if (confirmed) {
      newRemision();
    }
  }}
  disabled={loading}
>
  Limpiar campos
</button>

          

          <button
            type="button"
            className="remission-button remission-button-primary"
            onClick={finalizeAndOpenPdf}
            disabled={loading || catalogLoading}
          >
            {loading
              ? "Finalizando..."
              : "Finalizar e imprimir"}
          </button>
        </div>
          </div>

        </main>

        <aside className="remission-sidebar">
          <section className="remission-card remission-summary">
            <div className="remission-section-title">
              Resumen del envío
            </div>

            <dl>
              <div>
                <dt>Folio</dt>
                <dd>{folio}</dd>
              </div>
              <div>
                <dt>Proyecto</dt>
                <dd>{form.proyecto}</dd>
              </div>
              <div>
                <dt>Municipio</dt>
                <dd>
  {isTarjetasCirculacion
    ? "Gobierno del Estado de Quintana Roo"
    : form.municipio}
</dd>
              </div>
              <div>
                <dt>Oficina</dt>
                <dd>
  {isTarjetasCirculacion
    ? "SATQ"
    : form.oficina}
</dd>
              </div>
              <div>
                <dt>Modelo de impresora</dt>
                <dd>{selectedModel?.nombre || "Sin asignar"}</dd>
              </div>
              <div>
                <dt>Supervisor</dt>
                <dd>
  {isTarjetasCirculacion
    ? "Se asentará en recepción"
    : form.supervisor}
</dd>
              </div>
              <div>
                <dt>Transporte</dt>
                <dd>Paquetería</dd>
              </div>
              <div>
                <dt>Empresa de paquetería</dt>
                <dd>
                  {form.paqueteria ||
                    "Pendiente de asignación"}
                </dd>
              </div>
              <div>
                <dt>Número de guía</dt>
                <dd>
                  {form.numeroGuia ||
                    "Pendiente de asignación"}
                </dd>
              </div>
              <div>
                <dt>Partidas</dt>
                <dd>{items.length}</dd>
              </div>
              <div>
                <dt>Total enviado</dt>
                <dd>
                  {totalUnits.toLocaleString("es-MX")} unidades
                </dd>
              </div>
            </dl>

            <div
              className={
                hasInvalidFolioRange
                  ? "remission-status remission-status-error"
                  : "remission-status remission-status-ready"
              }
            >
              {hasInvalidFolioRange
                ? "Rango de folios pendiente"
                : "Listo para generar remisión"}
            </div>
          </section>
        </aside>
      </div>

      <section
        className={`remission-card remission-history remission-history-tab-${historyTab}`}
      >
        <div className="remission-section-heading">
          <div className="remission-section-title">
            Historial de remisiones
          </div>

          <button
            type="button"
            className="remission-button remission-button-secondary"
            onClick={loadHistory}
            disabled={historyLoading}
          >
            {historyLoading ? "Actualizando..." : "Actualizar"}
          </button>
        </div>

        {/* REMISSION_TABS_PASO_1_NAV */}
        <nav
          className="remission-history-tabs"
          aria-label="Secciones del historial de remisiones"
        >
          <button
            type="button"
            className={historyTab === "resumen" ? "active" : ""}
            onClick={() => setHistoryTab("resumen")}
          >
            Resumen
          </button>

          <button
            type="button"
            className={historyTab === "remisiones" ? "active" : ""}
            onClick={() => setHistoryTab("remisiones")}
          >
            Remisiones
          </button>

          <button
            type="button"
            className={historyTab === "distribucion" ? "active" : ""}
            onClick={() => setHistoryTab("distribucion")}
          >
            Distribucion
          </button>
        </nav>

        {historyTab === "distribucion" && (
          <section className="remission-distribution-panel">
            <div className="remission-distribution-summary">
              <div>
                <span>Remisiones</span>
                <strong>
                  {distributionTotals.remisiones.toLocaleString("es-MX")}
                </strong>
              </div>

              <div>
                <span>Tarjetas</span>
                <strong>
                  {distributionTotals.plasticos.toLocaleString("es-MX")}
                </strong>
              </div>

              <div>
                <span>Ribbon</span>
                <strong>
                  {distributionTotals.ribbon.toLocaleString("es-MX")}
                </strong>
              </div>

              <div>
                <span>Laminador</span>
                <strong>
                  {distributionTotals.laminador.toLocaleString("es-MX")}
                </strong>
              </div>

              <div>
                <span>Retransfer</span>
                <strong>
                  {distributionTotals.retransfer.toLocaleString("es-MX")}
                </strong>
              </div>

              <div>
                <span>Rollos</span>
                <strong>
                  {distributionTotals.rollos.toLocaleString("es-MX")}
                </strong>
              </div>
            </div>

            <div className="remission-distribution-heading">
              <div>
                <strong>Material acumulado por oficina</strong>
                <span>
                  No incluye borradores ni remisiones canceladas.
                </span>
              </div>

              <span>
                {distributionByMunicipality.length} oficina(s)
              </span>
            </div>

            {distributionByMunicipality.length === 0 ? (
              <div className="remission-distribution-empty">
                No hay material distribuido con los filtros actuales.
              </div>
            ) : (
              <div className="remission-distribution-table-wrap">
                <table className="remission-distribution-table">
                  <thead>
                    <tr>
                      <th>Municipio</th>
                      <th>Oficina</th>
                      <th>Modelo</th>
                      <th>Remisiones</th>
                      <th>Tarjetas</th>
                      <th>Ribbon</th>
                      <th>Laminador</th>
                      <th>Retransfer</th>
                      <th>Rollos</th>
                    </tr>
                  </thead>

                  <tbody>
                    {distributionByMunicipality.map((row) => (
                      <tr key={`${row.municipio}-${row.oficina}`}>
                        <td>
                          <strong>{row.municipio}</strong>
                        </td>

                        <td>
                          <strong>{row.oficina}</strong>
                        </td>

                        <td>
                          <span
                            className={`remission-printer-label ${
                              row.modelo === "SMART 51L"
                                ? "smart"
                                : row.modelo === "K30 SWITF PRO"
                                ? "k30"
                                : row.modelo === "SMART / K30"
                                ? "mixed"
                                : ""
                            }`}
                          >
                            {row.modelo}
                          </span>
                        </td>


                        <td>
                          {row.remisiones.toLocaleString("es-MX")}
                        </td>

                        <td>
                          {row.plasticos.toLocaleString("es-MX")}
                        </td>

                        <td>
                          {row.ribbon.toLocaleString("es-MX")}
                        </td>

                        <td>
                          {row.laminador.toLocaleString("es-MX")}
                        </td>

                        <td>
                          {row.soloSmart
                            ? "N/A"
                            : row.retransfer.toLocaleString("es-MX")}
                        </td>

                        <td>
                          {row.rollos.toLocaleString("es-MX")}
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      <th>TOTAL</th>
                      <th>-</th>
                      <th>-</th>

                      <th>
                        {distributionTotals.remisiones.toLocaleString("es-MX")}
                      </th>

                      <th>
                        {distributionTotals.plasticos.toLocaleString("es-MX")}
                      </th>

                      <th>
                        {distributionTotals.ribbon.toLocaleString("es-MX")}
                      </th>

                      <th>
                        {distributionTotals.laminador.toLocaleString("es-MX")}
                      </th>

                      <th>
                        {distributionTotals.retransfer.toLocaleString("es-MX")}
                      </th>

                      <th>
                        {distributionTotals.rollos.toLocaleString("es-MX")}
                      </th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </section>
        )}

        <div className="remission-history-monitor">
          <div className="remission-history-kpis">
            <div>
              <span>Resultados</span>
              <strong>{historyKpis.total}</strong>
            </div>

            <div>
              <span>Pendientes de guía</span>
              <strong>{historyKpis.pendientesGuia}</strong>
            </div>

            <div>
              <span>En tránsito</span>
              <strong>{historyKpis.enTransito}</strong>
            </div>

            <div>
              <span>Entregadas</span>
              <strong>{historyKpis.entregadas}</strong>
            </div>

            <div>
              <span>Unidades</span>
              <strong>
                {historyKpis.unidades.toLocaleString("es-MX")}
              </strong>
            </div>
          </div>

          <div className="remission-history-filters">
            <label>
              <span>Fecha desde</span>
              <input
                type="date"
                value={historyFilters.fechaDesde}
                onChange={(event) =>
                  updateHistoryFilter(
                    "fechaDesde",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>Fecha hasta</span>
              <input
                type="date"
                value={historyFilters.fechaHasta}
                onChange={(event) =>
                  updateHistoryFilter(
                    "fechaHasta",
                    event.target.value
                  )
                }
              />
            </label>

            <label>
              <span>Proyecto</span>
              <select
                value={historyFilters.proyecto}
                onChange={(event) =>
                  updateHistoryFilter(
                    "proyecto",
                    event.target.value
                  )
                }
              >
                <option value="">Todos</option>
                {historyOptions.proyectos.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Municipio</span>
              <select
                value={historyFilters.municipio}
                onChange={(event) =>
                  updateHistoryFilter(
                    "municipio",
                    event.target.value
                  )
                }
              >
                <option value="">Todos</option>
                {historyOptions.municipios.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Oficina</span>
              <select
                value={historyFilters.oficina}
                onChange={(event) =>
                  updateHistoryFilter(
                    "oficina",
                    event.target.value
                  )
                }
              >
                <option value="">Todas</option>
                {historyOptions.oficinas.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Estatus</span>
              <select
                value={historyFilters.status}
                onChange={(event) =>
                  updateHistoryFilter(
                    "status",
                    event.target.value
                  )
                }
              >
                <option value="">Todos</option>
                <option value="BORRADOR">Borrador</option>
                <option value="PENDIENTE_GUIA">
                  Pendiente de guía
                </option>
                <option value="EN_TRANSITO">
                  En tránsito
                </option>
                <option value="ENTREGADA">
                  Entregada
                </option>
                <option value="CANCELADA">
                  Cancelada
                </option>
              </select>
            </label>

            <label>
              <span>Supervisor</span>
              <select
                value={historyFilters.supervisor}
                onChange={(event) =>
                  updateHistoryFilter(
                    "supervisor",
                    event.target.value
                  )
                }
              >
                <option value="">Todos</option>
                {historyOptions.supervisores.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Paquetería</span>
              <select
                value={historyFilters.paqueteria}
                onChange={(event) =>
                  updateHistoryFilter(
                    "paqueteria",
                    event.target.value
                  )
                }
              >
                <option value="">Todas</option>
                {historyOptions.paqueterias.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </label>

            <label className="remission-history-search">
              <span>Búsqueda general</span>
              <input
                type="search"
                placeholder="Folio, guía, oficina, supervisor..."
                value={historyFilters.busqueda}
                onChange={(event) =>
                  updateHistoryFilter(
                    "busqueda",
                    event.target.value
                  )
                }
              />
            </label>
          </div>

          <div className="remission-history-filter-actions">
            <span>
              Mostrando {filteredHistory.length} de{" "}
              {history.length} remisiones
            </span>

            <div>
              <button
                type="button"
                onClick={clearHistoryFilters}
              >
                Limpiar filtros
              </button>

              <button
                type="button"
                onClick={exportHistoryExcel}
                disabled={!filteredHistory.length}
              >
                Exportar Excel
              </button>

              <button
                type="button"
                onClick={exportHistoryPdf}
                disabled={!filteredHistory.length}
              >
                Exportar PDF
              </button>
            </div>
          </div>
        </div>

        {historyLoading ? (
          <div className="remission-empty">
            Cargando remisiones...
          </div>
        ) : filteredHistory.length === 0 ? (
          <div className="remission-empty">
            No hay remisiones que coincidan con los filtros.
          </div>
        ) : (
          <div className="remission-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Folio</th>
                  <th>Fecha</th>
                  <th>Proyecto</th>
                  <th>Municipio</th>
                  <th>Oficina</th>
                  <th>Supervisor</th>
                  <th>Partidas</th>
                  <th>Unidades</th>
                  <th>Estatus</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {filteredHistory.map((record) => (
                  <tr key={record.id}>
                    <td>{record.folio}</td>
                    <td>{record.fechaEnvio}</td>
                    <td>{record.proyecto}</td>
                    <td>{record.municipio}</td>
                    <td>{record.oficina}</td>
                    <td>{record.supervisor}</td>
                    <td>{record.totalPartidas}</td>
                    <td>
                      {Number(
                        record.totalUnidades || 0
                      ).toLocaleString("es-MX")}
                    </td>
                    <td>
                      <span
                        className={`remission-history-status ${String(
                          record.status || ""
                        ).toLowerCase()}`}
                      >
                        {statusLabel(record.status)}
                      </span>
                    </td>
                    <td>
                      <div className="remission-history-actions">
                        {record.status === "BORRADOR" && (
                          <button
                            type="button"
                            onClick={() => editDraft(record)}
                          >
                            Editar
                          </button>
                        )}

                        {record.pdfKey && (
                          <button
                            type="button"
                            onClick={() => openPdf(record.id)}
                          >
                            Ver PDF
                          </button>
                        )}

                        {(record.status === "PENDIENTE_GUIA" ||
                          record.status === "EN_TRANSITO") && (
                          <button
                            type="button"
                            onClick={() => addGuide(record)}
                          >
                            {record.numeroGuia
                              ? "Actualizar guía"
                              : "Agregar guía"}
                          </button>
                        )}

                        {record.status === "EN_TRANSITO" && (
                          <button
                            type="button"
                            onClick={() =>
                              markDelivered(record)
                            }
                          >
                            Marcar entregada
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {guideModal.open && (
        <div
          className="guide-modal-backdrop"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget &&
              !loading
            ) {
              closeGuideModal();
            }
          }}
        >
          <section
            className="guide-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guide-modal-title"
          >
            <header className="guide-modal-header">
              <div>
                <span className="guide-modal-kicker">
                  Seguimiento logístico
                </span>

                <h2 id="guide-modal-title">
                  {guideModal.record?.numeroGuia
                    ? "Actualizar número de guía"
                    : "Registrar número de guía"}
                </h2>

                <p>
                  Remisión{" "}
                  <strong>
                    {guideModal.record?.folio || ""}
                  </strong>
                </p>
              </div>

              <button
                type="button"
                className="guide-modal-close"
                onClick={closeGuideModal}
                disabled={loading}
                aria-label="Cerrar modal"
              >
                ×
              </button>
            </header>

            <div className="guide-modal-body">
              {guideModal.record?.paqueteria ? (
                <div className="guide-modal-provider">
                  <span>Empresa de paquetería</span>

                  <strong>
                    {guideModal.record.paqueteria}
                  </strong>

                  <small>
                    Empresa registrada al generar la remisión.
                  </small>
                </div>
              ) : (
                <label className="guide-modal-field">
                  <span>Empresa de paquetería *</span>

                  <input
                    type="text"
                    value={guideModal.paqueteria}
                    onChange={(event) =>
                      updateGuideModal(
                        "paqueteria",
                        event.target.value
                      )
                    }
                    placeholder="Ej. DHL, FedEx, Estafeta"
                    maxLength="120"
                    disabled={loading}
                    autoFocus
                  />

                  <small className="guide-modal-help">
                    No fue capturada al generar la remisión.
                  </small>
                </label>
              )}

              <label className="guide-modal-field">
                <span>Número de guía *</span>

                <input
                  type="text"
                  value={guideModal.numeroGuia}
                  onChange={(event) =>
                    updateGuideModal(
                      "numeroGuia",
                      event.target.value
                    )
                  }
                  placeholder="Captura el número de rastreo"
                  maxLength="120"
                  disabled={loading}
                  autoFocus={Boolean(
                    guideModal.record?.paqueteria
                  )}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      saveGuide();
                    }

                    if (
                      event.key === "Escape" &&
                      !loading
                    ) {
                      closeGuideModal();
                    }
                  }}
                />
              </label>

              {guideModal.error && (
                <div
                  className="guide-modal-error"
                  role="alert"
                >
                  {guideModal.error}
                </div>
              )}
            </div>

            <footer className="guide-modal-footer">
              <button
                type="button"
                className="guide-modal-button guide-modal-button-secondary"
                onClick={closeGuideModal}
                disabled={loading}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="guide-modal-button guide-modal-button-primary"
                onClick={saveGuide}
                disabled={loading}
              >
                {loading
                  ? "Guardando..."
                  : "Guardar guía"}
              </button>
            </footer>
          </section>
        </div>
      )}

      {pdfPreview.open && (
        <div
          className="pdf-preview-backdrop"
          role="presentation"
        >
          <section
            className="pdf-preview-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pdf-preview-title"
          >
            <header className="pdf-preview-header">
              <div>
                <span>Remisión finalizada</span>

                <h2 id="pdf-preview-title">
                  Vista previa para impresión
                </h2>

                <p>
                  Folio{" "}
                  <strong>{pdfPreview.folio}</strong>
                </p>
              </div>

              <button
                type="button"
                className="pdf-preview-close"
                onClick={closePdfPreview}
                disabled={pdfPreview.printing}
                aria-label="Cerrar vista previa"
              >
                ×
              </button>
            </header>

            <div className="pdf-preview-content">
              <iframe
                id="remission-pdf-preview-frame"
                className="pdf-preview-frame"
                src={pdfPreview.url}
                title={`Remisión ${pdfPreview.folio}`}
              />
            </div>

            <footer className="pdf-preview-footer">
              <span>
                Verifica la información antes de imprimir.
              </span>

              <div>
                <button
                  type="button"
                  className="pdf-preview-button pdf-preview-button-secondary"
                  onClick={closePdfPreview}
                  disabled={pdfPreview.printing}
                >
                  Cerrar
                </button>

                <button
                  type="button"
                  className="pdf-preview-button pdf-preview-button-primary"
                  onClick={printPdfPreview}
                  disabled={pdfPreview.printing}
                >
                  {pdfPreview.printing
                    ? "Abriendo impresión..."
                    : "Imprimir"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}

    </div>
  );
}
